/**
 * Addon feature flags – runtime config
 *
 * Frontend se ptá backendu GET /api/v1/addons a dostane { inventory: bool }.
 * Backend čte z env ADDON_INVENTORY=true – nastavuje se per-klient v .env.
 *
 * Workflow aktivace pro zákazníka:
 *   1. Zákazník si připlátí → přidáš do .env klienta: ADDON_INVENTORY=true
 *   2. Restart API kontejneru → hotovo (frontend si stav přečte automaticky)
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface PackageInfo { id: string; name: string; description: string }
export interface PackageLimits {
  /** 0 = bez omezení */
  users: number; branches: number; vehicles: number; storageMb: number;
}

export interface AddonsConfig {
  inventory: boolean;
  multiBranch: boolean;
  /** Servis aut – zakázky, ceník prací, objednávky dílů, reklamace */
  service: boolean;
  /** E-podpis dokumentů – podpis zákazníka na tabletu / mobilu přes QR */
  esign: boolean;
  /** Poprodejní služby – připomínky STK, garančního a servisního termínu klientům po prodeji */
  aftersales: boolean;
  /** Mobilní rozhraní (/m) – zjednodušené dlaždice pro telefon v terénu */
  mobile: boolean;
  /** Balíček přiřazený v superadminu; null = bez balíčku / individuální */
  package: PackageInfo | null;
  limits: PackageLimits;
}

const DEFAULT: AddonsConfig = {
  inventory: false, multiBranch: false, service: false, esign: false, aftersales: false, mobile: false,
  package: null,
  limits: { users: 0, branches: 0, vehicles: 0, storageMb: 0 },
};

const addonsQuery = {
  queryKey: ['addons-config'],
  queryFn: () =>
    apiClient
      .get<{ success: boolean; data: AddonsConfig }>('/addons')
      .then((r) => r.data.data),
  staleTime: Infinity,   // nemění se za běhu
  retry: false,
} as const;

/** true, dokud se konfigurace modulů ještě načítá (do té doby je vše „vypnuto“). */
export function useAddonsLoading(): boolean {
  return useQuery<AddonsConfig>(addonsQuery).isLoading;
}

export function useAddons(): AddonsConfig {
  const { data } = useQuery<AddonsConfig>(addonsQuery);
  return data ? { ...DEFAULT, ...data, limits: { ...DEFAULT.limits, ...(data.limits ?? {}) } } : DEFAULT;
}

export type AddonKey = keyof AddonsConfig;
